import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  useSensors,
  useSensor,
  PointerSensor,
  KeyboardSensor,
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import {
  Bars3Icon,
  ClipboardIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ShareIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import UserHeader from '../components/UserHeader'
import FolderSidebar from '../components/FolderSidebar'
import UrlCard from '../components/UrlCard'
import Pagination from '../components/Pagination'
import CreateUrlModal from '../components/bookmark/CreateUrlModal'
import ShareFolderModal from '../components/bookmark/ShareFolderModal'
import SharedFoldersModal from '../components/bookmark/SharedFoldersModal'
import SortDropdown from '../components/bookmark/SortDropdown'
import BookmarkItem from '../components/bookmark/BookmarkItem'
import BulkActionBar from '../components/bookmark/BulkActionBar'
import BulkMoveModal from '../components/bookmark/BulkMoveModal'
import {
  BookmarkEmptyState,
  BookmarkErrorState,
} from '../components/bookmark/BookmarkListStates'
import toast from 'react-hot-toast'
import QuickAddBar from '../components/bookmark/QuickAddBar'
import CompactUrlCard from '../components/bookmark/CompactUrlCard'
import LinkDetailDrawer from '../components/bookmark/LinkDetailDrawer'
import ViewModeToggle from '../components/bookmark/ViewModeToggle'
import { bookmarkApi } from '../../../shared/services/bookmarkApi'
import ConfirmDialog from '../../../shared/components/ConfirmDialog'
import { useTranslation } from 'react-i18next'
import { useDebouncedValue } from '../../../shared/hooks/useDebouncedValue'
import {
  parseSecurityFilter,
  useBookmarkFilters,
} from '../hooks/useBookmarkFilters'
import { useBookmarkSelection } from '../hooks/useBookmarkSelection'
import { useBookmarkedUrls } from '../hooks/useBookmarkedUrls'
import { useLinkFeatures, useLinkWeeklyClicks } from '../hooks/useLinkDetail'
import {
  useBookmarkMutations,
  BOOKMARK_LIST_KEY,
} from '../hooks/useBookmarkMutations'
import type {
  BookmarkedUrl,
  BookmarkViewMode,
  BulkAction,
} from '../../../types/bookmark'

const SEARCH_DEBOUNCE_MS = 300
const MOBILE_BREAKPOINT = 768
const VIEW_MODE_STORAGE_KEY = 'bookmark-view-mode'
const COPY_TOAST_DURATION_MS = 2000

/** 보기 모드별 목록 배치. 렌더마다 만들지 않도록 밖에 둔다 */
const LIST_CLASS: Record<BookmarkViewMode, string> = {
  list: 'space-y-3',
  compact:
    'grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4 5xl:grid-cols-6',
  grid: 'grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6 xl:grid-cols-3 3xl:grid-cols-4 5xl:grid-cols-5',
}

type PendingBulk = { action: BulkAction; isFavorite?: boolean } | null

const VIEW_MODES: BookmarkViewMode[] = ['grid', 'compact', 'list']

function readStoredViewMode(): BookmarkViewMode {
  try {
    const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY)
    return VIEW_MODES.includes(stored as BookmarkViewMode)
      ? (stored as BookmarkViewMode)
      : 'grid'
  } catch {
    return 'grid'
  }
}

/** 북마크 관리 화면 — 폴더·태그·검색 필터와 일괄 작업을 제공한다 */
export default function BookmarkPage() {
  const { t } = useTranslation()
  const {
    filters,
    hasActiveFilters,
    selectFolder,
    toggleFavorites,
    toggleTag,
    clearTags,
    setSecurity,
    setPreset,
    changeSearch,
    changeSort,
    changePage,
    changeLimit,
    clearFilters,
  } = useBookmarkFilters()

  // 검색 입력은 즉시 반영하되 요청은 디바운스한다 (타자마다 호출 방지)
  const [searchInput, setSearchInput] = useState(filters.search)
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS)

  const [isMobile, setIsMobile] = useState(
    () => window.innerWidth < MOBILE_BREAKPOINT
  )
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false)
  const [viewMode, setViewMode] = useState<BookmarkViewMode>(readStoredViewMode)
  const [copySuccess, setCopySuccess] = useState<string | null>(null)
  const [draggedUrl, setDraggedUrl] = useState<BookmarkedUrl | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showShareFolderModal, setShowShareFolderModal] = useState(false)
  const [showSharedFolders, setShowSharedFolders] = useState(false)
  const [showBulkMove, setShowBulkMove] = useState(false)
  const [pendingBulk, setPendingBulk] = useState<PendingBulk>(null)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  const [pendingDeleteTagId, setPendingDeleteTagId] = useState<string | null>(
    null
  )
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [detailUrlId, setDetailUrlId] = useState<string | null>(null)

  useEffect(() => {
    if (debouncedSearch !== filters.search) {
      changeSearch(debouncedSearch)
    }
  }, [debouncedSearch, filters.search, changeSearch])

  useEffect(() => {
    const handleResize = () =>
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // 폴더 사이드바가 발행하는 이동 이벤트
  useEffect(() => {
    const handleFolderNavigate = (event: Event) => {
      const { folderId } = (event as CustomEvent<{ folderId: string }>).detail
      selectFolder(folderId)
      setIsMobileSidebarOpen(false)
    }
    window.addEventListener('folderNavigate', handleFolderNavigate)
    return () =>
      window.removeEventListener('folderNavigate', handleFolderNavigate)
  }, [selectFolder])

  const query = useMemo(
    () => ({
      folderId: filters.folderId,
      tags: filters.tags,
      search: filters.search,
      favorite: filters.favorite,
      security: filters.security,
      preset: filters.preset,
      page: filters.page,
      limit: filters.limit,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
    }),
    [filters]
  )

  const queryClient = useQueryClient()
  const { data, isLoading, isError, error, refetch } = useBookmarkedUrls(query)
  const urls = useMemo(() => data?.urls ?? [], [data])
  const visibleIds = useMemo(() => urls.map(url => url._id), [urls])
  const selection = useBookmarkSelection(visibleIds)

  // 링크를 만들거나 고친 뒤 목록·태그를 다시 받아온다.
  // 예전에는 폴더 사이드바에만 신호를 보내서, 목록 쿼리는 staleTime(30초)
  // 이 지나기 전까지 갱신되지 않았다. 방금 만든 링크가 수동 새로고침
  // 전에는 보이지 않던 원인이다.
  const onSideEffect = useCallback(() => {
    // 현재 필터뿐 아니라 다른 필터 조합의 캐시도 함께 무효화한다
    queryClient.invalidateQueries({ queryKey: BOOKMARK_LIST_KEY })
    queryClient.invalidateQueries({ queryKey: ['tags'] })
    setRefreshTrigger(previous => previous + 1)
  }, [queryClient])
  const { remove, update, toggleFavorite, moveToFolder, bulk, deleteTag } =
    useBookmarkMutations({
      onSideEffect,
    })

  // 패널은 목록 캐시의 값을 그대로 본다. 저장 후 캐시가 갱신되면
  // 패널도 함께 바뀌므로 따로 상태를 복사해 두지 않는다.
  const detailUrl = useMemo(
    () => urls.find(item => item._id === detailUrlId) ?? null,
    [urls, detailUrlId]
  )
  const weekly = useLinkWeeklyClicks(detailUrl?.shortCode ?? null)
  const linkFeatures = useLinkFeatures()

  const closeDetail = useCallback(() => setDetailUrlId(null), [])

  const handleDetailSave = useCallback(
    (input: Record<string, unknown>) => {
      if (detailUrlId) {
        update.mutate({ urlId: detailUrlId, input })
      }
    },
    [detailUrlId, update]
  )

  // 목록에서 사라진 링크(삭제·필터 변경)의 패널은 닫는다
  useEffect(() => {
    if (detailUrlId && !isLoading && !detailUrl) {
      setDetailUrlId(null)
    }
  }, [detailUrlId, detailUrl, isLoading])

  const handleViewModeChange = (mode: BookmarkViewMode) => {
    setViewMode(mode)
    try {
      localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode)
    } catch {
      // 저장 실패는 화면 동작에 영향을 주지 않는다
    }
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragStart = (event: DragStartEvent) => {
    setDraggedUrl(urls.find(url => url._id === event.active.id) ?? null)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setDraggedUrl(null)
    if (!over || active.id === over.id) {
      return
    }

    const overId = String(over.id)
    if (!overId.startsWith('folder-')) {
      return
    }

    const folderId = overId.replace('folder-', '')
    moveToFolder.mutate({
      urlId: String(active.id),
      folderId: folderId === 'all' ? null : folderId,
    })
  }

  const handleCopySuccess = (text: string) => {
    setCopySuccess(text)
    setTimeout(() => setCopySuccess(null), COPY_TOAST_DURATION_MS)
  }

  const [isQuickAdding, setIsQuickAdding] = useState(false)

  /**
   * 빠른 추가. 지금 보고 있는 폴더에 넣는다.
   * 폴더를 열어 둔 채 만들면 그 폴더에 들어가는 편이 자연스럽다.
   */
  const handleQuickAdd = useCallback(
    async (originalUrl: string) => {
      setIsQuickAdding(true)
      try {
        const result = await bookmarkApi.create({
          originalUrl,
          ...(filters.folderId ? { folderId: filters.folderId } : {}),
        })
        if (!result.success) {
          throw new Error(result.message || t('bookmark.quickAddError'))
        }
        toast.success(t('bookmark.quickAddSuccess'))
        onSideEffect()
      } catch (createError) {
        console.error('빠른 추가 실패:', createError)
        toast.error(
          createError instanceof Error && createError.message
            ? createError.message
            : t('bookmark.quickAddError')
        )
      } finally {
        setIsQuickAdding(false)
      }
    },
    [filters.folderId, onSideEffect, t]
  )

  const handleUrlUpdate = useCallback(() => onSideEffect(), [onSideEffect])

  const runBulk = (action: BulkAction, payload?: { isFavorite?: boolean }) => {
    bulk.mutate(
      {
        action,
        urlIds: selection.selectedIds,
        isFavorite: payload?.isFavorite,
      },
      { onSuccess: () => selection.clear() }
    )
  }

  const handleBulkAction = (
    action: BulkAction,
    payload?: { isFavorite?: boolean }
  ) => {
    // 삭제는 되돌릴 수 없으므로 확인을 받는다
    if (action === 'delete') {
      setPendingBulk({ action })
      return
    }
    runBulk(action, payload)
  }

  const viewTitle = filters.favorite
    ? t('bookmark.view.favorites')
    : filters.folderId
      ? t('bookmark.view.folder')
      : filters.tags.length > 0
        ? t('bookmark.view.tagFilter')
        : t('bookmark.view.all')

  const sidebarProps = {
    onFolderSelect: (folderId: string | null) => {
      selectFolder(folderId)
      setIsMobileSidebarOpen(false)
    },
    selectedFolder: filters.folderId,
    showFavorites: filters.favorite,
    onToggleFavorites: () => {
      toggleFavorites()
      setIsMobileSidebarOpen(false)
    },
    selectedTags: filters.tags,
    onTagClick: toggleTag,
    onClearTags: clearTags,
    onDeleteTag: (tagId: string) => setPendingDeleteTagId(tagId),
    refreshTrigger,
    onFolderChange: onSideEffect,
    securityFilter: filters.security,
    // FolderSidebar 는 일반 문자열을 넘기므로 허용된 값으로 좁혀서 받는다
    onSecurityFilterChange: (value: string | null) =>
      setSecurity(parseSecurityFilter(value)),
    presetFilter: filters.preset,
    onPresetFilterChange: setPreset,
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <UserHeader
          title={t('bookmark.title')}
          subtitle={t('bookmark.subtitle')}
        />

        <div className="flex h-[calc(100vh-120px)]">
          {isMobile ? (
            <AnimatePresence>
              {isMobileSidebarOpen && (
                <>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setIsMobileSidebarOpen(false)}
                    className="fixed inset-0 z-40 bg-black/50"
                  />
                  <motion.aside
                    initial={{ y: '100%' }}
                    animate={{ y: 0 }}
                    exit={{ y: '100%' }}
                    transition={{ type: 'spring', damping: 28, stiffness: 240 }}
                    // 모바일에서는 아래에서 올라오는 시트가 한 손으로 닿기 좋다
                    className="fixed inset-x-0 bottom-0 z-50 flex h-[78vh] flex-col rounded-t-2xl bg-white shadow-2xl dark:bg-gray-800"
                  >
                    <div className="flex justify-center pt-2">
                      <span className="h-1 w-10 rounded-full bg-gray-200 dark:bg-gray-600" />
                    </div>
                    <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {t('bookmark.folders')}
                      </h2>
                      <button
                        type="button"
                        aria-label={t('bookmark.closeSidebar')}
                        onClick={() => setIsMobileSidebarOpen(false)}
                        className="rounded-md p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <XMarkIcon className="h-5 w-5 text-gray-500" />
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                      <FolderSidebar {...sidebarProps} />
                    </div>
                  </motion.aside>
                </>
              )}
            </AnimatePresence>
          ) : (
            <FolderSidebar {...sidebarProps} />
          )}

          <div className="flex flex-1 flex-col overflow-hidden">
            <header className="border-b border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 md:p-6">
              <div className="mx-auto max-w-none 3xl:max-w-[1800px]">
                <div className="mb-4 flex items-center gap-3">
                  {isMobile && (
                    <button
                      type="button"
                      aria-label={t('bookmark.openSidebar')}
                      onClick={() => setIsMobileSidebarOpen(true)}
                      className="rounded-md p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <Bars3Icon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                    </button>
                  )}

                  <h1 className="flex-1 text-xl font-bold text-gray-900 dark:text-white md:text-2xl">
                    {viewTitle}
                    {data && (
                      <span className="ml-2 text-sm font-normal tabular-nums text-gray-500 dark:text-gray-400">
                        {t('bookmark.count', {
                          count: data.total.toLocaleString(),
                        })}
                      </span>
                    )}
                  </h1>

                  <div className="flex items-center gap-2">
                    <ViewModeToggle
                      value={viewMode}
                      onChange={handleViewModeChange}
                    />

                    <button
                      type="button"
                      onClick={() => setShowSharedFolders(true)}
                      className="hidden items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 md:flex"
                    >
                      <UserGroupIcon className="h-4 w-4" />
                      <span className="hidden lg:inline">
                        {t('bookmark.teamShare')}
                      </span>
                    </button>

                    {filters.folderId && (
                      <button
                        type="button"
                        onClick={() => setShowShareFolderModal(true)}
                        className="hidden items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 md:flex"
                      >
                        <ShareIcon className="h-4 w-4" />
                        <span className="hidden lg:inline">
                          {t('bookmark.folderShare')}
                        </span>
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => setShowCreateModal(true)}
                      className="flex items-center gap-2 rounded-md bg-primary-600 px-3 py-2 text-sm text-white transition hover:bg-primary-700 md:px-4"
                    >
                      <PlusIcon className="h-4 w-4" />
                      <span className="hidden sm:inline">
                        {t('bookmark.createLink')}
                      </span>
                      <span className="sm:hidden">
                        {t('bookmark.createShort')}
                      </span>
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="relative max-w-md flex-1">
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                    <label className="sr-only" htmlFor="bookmark-search">
                      {t('bookmark.search')}
                    </label>
                    <input
                      id="bookmark-search"
                      type="search"
                      value={searchInput}
                      onChange={event => setSearchInput(event.target.value)}
                      placeholder={t('bookmark.searchPlaceholder')}
                      className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-10 text-sm placeholder-gray-400 transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                    {searchInput && (
                      <button
                        type="button"
                        aria-label={t('bookmark.clearSearch')}
                        onClick={() => setSearchInput('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition hover:text-gray-600"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>

                  <SortDropdown
                    sortBy={filters.sortBy}
                    sortOrder={filters.sortOrder}
                    onSortChange={changeSort}
                  />

                  {hasActiveFilters && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchInput('')
                        clearFilters()
                      }}
                      className="whitespace-nowrap rounded-lg px-3 py-2 text-sm text-gray-500 underline transition hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      {t('bookmark.clearFilters')}
                    </button>
                  )}
                </div>
              </div>
            </header>

            <div className="flex-1 overflow-y-auto p-4 md:p-6">
              <div className="mx-auto flex max-w-none flex-col gap-4 3xl:max-w-[1800px]">
                <QuickAddBar
                  onSubmit={handleQuickAdd}
                  isSubmitting={isQuickAdding}
                />

                {isError ? (
                  <BookmarkErrorState
                    message={(error as Error | undefined)?.message}
                    onRetry={() => refetch()}
                  />
                ) : isLoading ? (
                  <div className="flex h-64 items-center justify-center">
                    <div
                      role="status"
                      aria-label={t('bookmark.loading')}
                      className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600"
                    />
                  </div>
                ) : urls.length === 0 ? (
                  <BookmarkEmptyState
                    query={filters.search}
                    hasOtherFilters={
                      Boolean(filters.folderId) ||
                      filters.favorite ||
                      filters.tags.length > 0 ||
                      filters.security !== null ||
                      filters.preset !== null
                    }
                    isFavoritesView={filters.favorite}
                    onCreate={() => setShowCreateModal(true)}
                    onClearQuery={() => {
                      setSearchInput('')
                      changeSearch('')
                    }}
                    onSearchEverywhere={() => {
                      // 검색어는 두고 범위만 넓힌다
                      clearFilters()
                      changeSearch(filters.search)
                      setSearchInput(filters.search)
                    }}
                    onClearFilters={() => {
                      setSearchInput('')
                      clearFilters()
                    }}
                  />
                ) : (
                  <div className={LIST_CLASS[viewMode]}>
                    {urls.map(url =>
                      viewMode === 'compact' ? (
                        <CompactUrlCard
                          key={url._id}
                          url={url}
                          isSelected={selection.isSelected(url._id)}
                          isSelectionMode={selection.isSelectionMode}
                          onToggleSelect={selection.toggle}
                          onCopySuccess={handleCopySuccess}
                          onOpenDetail={setDetailUrlId}
                        />
                      ) : (
                        <BookmarkItem
                          key={url._id}
                          url={url}
                          viewMode={viewMode}
                          isMobile={isMobile}
                          isSelected={selection.isSelected(url._id)}
                          isSelectionMode={selection.isSelectionMode}
                          onToggleSelect={selection.toggle}
                          onUpdate={handleUrlUpdate}
                          onDelete={setPendingDeleteId}
                          onCopySuccess={handleCopySuccess}
                          onTagClick={toggleTag}
                          onOpenDetail={setDetailUrlId}
                        />
                      )
                    )}
                  </div>
                )}

                {data && urls.length > 0 && (
                  <Pagination
                    currentPage={filters.page}
                    totalPages={data.totalPages || 1}
                    totalItems={data.total || 0}
                    itemsPerPage={filters.limit}
                    onPageChange={changePage}
                    onItemsPerPageChange={changeLimit}
                    isLoading={isLoading}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <LinkDetailDrawer
        url={detailUrl}
        features={linkFeatures.data}
        weekly={weekly.data}
        isWeeklyError={weekly.isError}
        isSaving={update.isPending}
        onClose={closeDetail}
        onSave={handleDetailSave}
        onDelete={setPendingDeleteId}
        onToggleFavorite={urlId => toggleFavorite.mutate(urlId)}
        onCopySuccess={handleCopySuccess}
      />

      <BulkActionBar
        selectedCount={selection.selectedIds.length}
        isAllSelected={selection.isAllSelected}
        isBusy={bulk.isPending}
        onSelectAll={selection.selectAll}
        onClear={selection.clear}
        onAction={handleBulkAction}
        onMoveRequest={() => setShowBulkMove(true)}
      />

      <DragOverlay>
        {draggedUrl && (
          <div className="rotate-6 opacity-90">
            <UrlCard
              url={draggedUrl}
              onUpdate={handleUrlUpdate}
              onDelete={setPendingDeleteId}
              onCopySuccess={handleCopySuccess}
            />
          </div>
        )}
      </DragOverlay>

      <AnimatePresence>
        {copySuccess && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            role="status"
            className="fixed bottom-20 right-4 z-50 flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-3 text-white shadow-lg"
          >
            <ClipboardIcon className="h-5 w-5" />
            {t('bookmark.copied')}
          </motion.div>
        )}
      </AnimatePresence>

      <CreateUrlModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onUrlCreated={() => {
          onSideEffect()
          setShowCreateModal(false)
        }}
        selectedFolder={filters.folderId}
      />

      {showShareFolderModal && filters.folderId && (
        <ShareFolderModal
          folderId={filters.folderId}
          onClose={() => setShowShareFolderModal(false)}
          onDone={onSideEffect}
        />
      )}

      {showSharedFolders && (
        <SharedFoldersModal onClose={() => setShowSharedFolders(false)} />
      )}

      <BulkMoveModal
        isOpen={showBulkMove}
        selectedCount={selection.selectedIds.length}
        isBusy={bulk.isPending}
        onCancel={() => setShowBulkMove(false)}
        onConfirm={folderId => {
          setShowBulkMove(false)
          bulk.mutate(
            { action: 'move', urlIds: selection.selectedIds, folderId },
            { onSuccess: () => selection.clear() }
          )
        }}
      />

      <ConfirmDialog
        isOpen={pendingBulk?.action === 'delete'}
        title={t('bookmark.deleteSelectedTitle', {
          count: String(selection.selectedIds.length),
        })}
        description={t('bookmark.deleteWarning')}
        confirmLabel={t('common.delete')}
        isBusy={bulk.isPending}
        onCancel={() => setPendingBulk(null)}
        onConfirm={() => {
          setPendingBulk(null)
          runBulk('delete')
        }}
      />

      <ConfirmDialog
        isOpen={!!pendingDeleteId}
        title={t('bookmark.deleteOneTitle')}
        description={t('bookmark.deleteWarning')}
        confirmLabel={t('common.delete')}
        isBusy={remove.isPending}
        onCancel={() => setPendingDeleteId(null)}
        onConfirm={() => {
          if (pendingDeleteId) {
            remove.mutate(pendingDeleteId)
          }
          setPendingDeleteId(null)
        }}
      />

      <ConfirmDialog
        isOpen={!!pendingDeleteTagId}
        title={t('bookmark.deleteTagTitle')}
        description={t('bookmark.deleteTagWarning')}
        confirmLabel={t('common.delete')}
        isBusy={deleteTag.isPending}
        onCancel={() => setPendingDeleteTagId(null)}
        onConfirm={() => {
          if (pendingDeleteTagId) {
            deleteTag.mutate(pendingDeleteTagId)
            if (filters.tags.includes(pendingDeleteTagId)) {
              toggleTag(pendingDeleteTagId)
            }
          }
          setPendingDeleteTagId(null)
        }}
      />
    </DndContext>
  )
}
